import { useQuery } from "@tanstack/react-query";
import type { AuthorRow, DeliveryReport } from "@aura/shared";
import { ed } from "~/components/editorial";
import { api } from "~/lib/api";
import { fmtInt, plural } from "~/lib/format";
import { CompareTable } from "./CompareTable";
import { botsQuery, exportHref, useBotsParam, useCompareParam } from "./lib";
import { BotsToggle, Controls, ReportShell, SectionHead, Segments } from "./parts";

// Per-author merged PRs for the same complete periods as the breakdown.
// Kept off the overview on purpose: it's for finding where to look.
export function AuthorsPage() {
  const { includeBots, setIncludeBots } = useBotsParam();
  const [compare, setCompare] = useCompareParam();
  const { data, isLoading, error } = useQuery({
    queryKey: ["delivery-authors", compare, includeBots],
    queryFn: () =>
      api.get<{ data: { compare: DeliveryReport["compare"]; rows: AuthorRow[] } }>(
        `/delivery/authors?compare=${compare}${botsQuery(includeBots)}`,
      ),
    staleTime: 5 * 60 * 1000,
  });
  const c = data?.data.compare;
  const rows = data?.data.rows ?? [];
  const active = rows.filter((r) => r.current.merged > 0).length;

  return (
    <ReportShell meta={c ? `${c.current.label} vs. ${c.previous.label}` : undefined}>
      <Controls>
        <Segments
          label="Compare"
          value={compare}
          onChange={setCompare}
          options={[
            { value: "month", label: "Month" },
            { value: "quarter", label: "Quarter" },
          ]}
        />
        <BotsToggle includeBots={includeBots} onChange={setIncludeBots} />
      </Controls>
      <div className={ed.kicker}>Engineering delivery · by author</div>
      <h1 className={ed.headlineCompact}>
        {isLoading || !c ? "Merged PRs by author" : `${plural(active, "author")} merged PRs in ${c.current.short}`}
      </h1>
      <p className={ed.dek}>
        PR counts favor small PRs, and AI tools make small PRs cheap. Use this view to find where to
        look, a repo or a person whose review share dropped, not to rank people.
      </p>

      {isLoading ? (
        <div className={ed.loading}>Loading authors…</div>
      ) : error || !c ? (
        <div className={ed.loading}>Couldn't load the author view.</div>
      ) : (
        <>
          <SectionHead title="Merged PRs by author" href={exportHref("authors", includeBots, compare)} />
          <CompareTable
            nameHead="Author"
            periods={{ previous: c.previous.short, current: c.current.short }}
            rows={rows}
            extra={[
              { head: "Login", cell: (r) => <span data-dim>{r.login ?? "–"}</span> },
              { head: `Median lines, ${c.current.short}`, cell: (r) => <span className="num">{fmtInt(r.medianLinesChanged)}</span> },
            ]}
          />
          <p className={ed.methodNarrow}>
            {fmtInt(rows.length)} authors with a merged PR in either period. Median lines is additions
            plus deletions per merged PR.
          </p>
        </>
      )}
    </ReportShell>
  );
}
